import React, { type ChangeEvent, type ReactElement, useEffect, useMemo, useRef, useState } from 'react'
import { nanoid } from 'nanoid'
import { swalError, swalSuccess, swalSureDelete } from '../../../functions/swalOptions'
import {
    DEFAULT_SPONSORED_PARTNERS_CONFIG,
    fetchSponsoredPartnersConfig,
    saveSponsoredPartnersConfig,
    uploadSponsoredPartnerImage
} from './sponsoredPartners.api'
import type {
    SponsoredModalSurface,
    SponsoredPartner,
    SponsoredPartnerCalculatedStatus,
    SponsoredPartnerChatConfig,
    SponsoredPartnerChatMessage,
    SponsoredPartnerInlineVariant,
    SponsoredPartnerPlanId,
    SponsoredPartnersRemoteConfig
} from './sponsoredPartners.types'
import './SponsoredPartnersAdminPage.scss'

type SurfaceKey = keyof SponsoredPartner['surfaces']
type UploadState = Record<string, boolean>
type UploadErrors = Record<string, string | undefined>
type EditorMode = 'idle' | 'create' | 'edit'
type SurfacePresentation = {
    adminTitle: string
    description: string
}

type SponsoredPartnerPlan = {
    title: string
    priceLabel: string
    commitmentTerms: string
    allowedSurfaces: Record<SurfaceKey, boolean>
    networkLimitLabel: string
    activeAdsLimit: number
    sponsoredChatLimitLabel?: string
    exploreHeroLimitLabel?: string
}

const SPONSORED_PARTNER_PLANS: Record<SponsoredPartnerPlanId, SponsoredPartnerPlan> = {
    silver: {
        title: 'Silver Plan',
        priceLabel: 'AED 10 / day',
        commitmentTerms: '1-Month Contract',
        allowedSurfaces: {
            modalCard: true,
            unitsCatalogueInline: false,
            profileListingsInline: false,
            sponsoredChat: false,
            exploreHero: false
        },
        networkLimitLabel: 'Shared Room (Max 99 active ads)',
        activeAdsLimit: 99
    },
    gold: {
        title: 'Gold Plan',
        priceLabel: 'AED 30 / day',
        commitmentTerms: '1-Month Contract',
        allowedSurfaces: {
            modalCard: true,
            unitsCatalogueInline: true,
            profileListingsInline: true,
            sponsoredChat: false,
            exploreHero: false
        },
        networkLimitLabel: 'Premium Room (Max 20 active ads)',
        activeAdsLimit: 20
    },
    platinum: {
        title: 'Platinum Plan',
        priceLabel: 'AED 2,499 / week',
        commitmentTerms: 'Weekly Rolling Framework',
        allowedSurfaces: {
            modalCard: true,
            unitsCatalogueInline: true,
            profileListingsInline: true,
            sponsoredChat: true,
            exploreHero: true
        },
        sponsoredChatLimitLabel: '3 Days / Week',
        exploreHeroLimitLabel: 'Static Block',
        networkLimitLabel: '100% Platform Monopoly (Only 1 ad allowed)',
        activeAdsLimit: 1
    }
}

const SURFACE_LABELS: Record<SurfaceKey, string> = {
    modalCard: 'Contextual Classified Text Ads',
    unitsCatalogueInline: 'In-Feed Dynamic Display Banner Ads - Units Catalogue',
    profileListingsInline: 'In-Feed Dynamic Display Banner Ads - Profile Listings',
    sponsoredChat: 'Sponsored Direct Messaging Ads',
    exploreHero: 'Native Dashboard Banner Ads'
}

const SURFACE_SHORT_LABELS: Record<SurfaceKey, string> = {
    modalCard: 'Modal Card',
    unitsCatalogueInline: 'Units Catalogue Inline',
    profileListingsInline: 'Profile Listings Inline',
    sponsoredChat: 'Sponsored Chat',
    exploreHero: 'Explore Hero'
}

const SURFACE_PRESENTATION: Record<SurfaceKey, SurfacePresentation> = {
    exploreHero: {
        adminTitle: 'Native Dashboard Banner',
        description: 'Appears in the main dashboard hero placement.'
    },
    unitsCatalogueInline: {
        adminTitle: 'In-Feed Catalogue Banner',
        description: 'Appears between listings in the units catalogue.'
    },
    profileListingsInline: {
        adminTitle: 'In-Feed Profile Banner',
        description: 'Appears between listings on profile pages.'
    },
    modalCard: {
        adminTitle: 'Contextual Classified Text Ad',
        description: 'Appears inside selected unit, viewing, offer, or advertise modals.'
    },
    sponsoredChat: {
        adminTitle: 'Sponsored Direct Message',
        description: 'Appears as a sponsored conversation in chat.'
    }
}

const SURFACE_KEYS: SurfaceKey[] = ['modalCard', 'unitsCatalogueInline', 'profileListingsInline', 'sponsoredChat', 'exploreHero']

const INLINE_VARIANTS: Array<{ value: SponsoredPartnerInlineVariant, label: string }> = [
    { value: 'yellow-curve', label: 'Yellow Curve' },
    { value: 'red-arrow', label: 'Red Arrow' },
    { value: 'green-wave', label: 'Green Wave' },
    { value: 'blue-curve', label: 'Blue Curve' },
    { value: 'plain', label: 'Plain' }
]

const MODAL_SURFACES: Array<{ value: SponsoredModalSurface, label: string }> = [
    { value: 'unit', label: 'Unit' },
    { value: 'viewing', label: 'Viewing' },
    { value: 'offer', label: 'Offer' },
    { value: 'advertise', label: 'Advertise' }
]

const blankPartner = (): SponsoredPartner => ({
    id: '',
    partnerName: '',
    enabled: false,
    previewOnly: false,
    websiteUrl: '',
    surfaces: {}
})

const getPlan = (planId?: SponsoredPartnerPlanId): SponsoredPartnerPlan | undefined => (
    planId != null ? SPONSORED_PARTNER_PLANS[planId] : undefined
)

const getPlanTitle = (planId?: SponsoredPartnerPlanId): string => (
    getPlan(planId)?.title ?? 'No plan selected / Legacy'
)

const getPlanPriceLabel = (planId?: SponsoredPartnerPlanId): string => (
    getPlan(planId)?.priceLabel ?? '-'
)

const getPlanShortLabel = (planId?: SponsoredPartnerPlanId): string => (
    planId == null ? 'Legacy' : SPONSORED_PARTNER_PLANS[planId].title.replace(' Plan', '')
)

const getPlanIncludedSurfaceNames = (plan: SponsoredPartnerPlan): string => (
    SURFACE_KEYS
        .filter((surfaceKey) => plan.allowedSurfaces[surfaceKey])
        .map((surfaceKey) => SURFACE_PRESENTATION[surfaceKey].adminTitle)
        .join(', ')
)

const isSurfaceAllowedForPlan = (partner: SponsoredPartner, surface: SurfaceKey): boolean => {
    const plan = getPlan(partner.planId)
    return plan == null ? false : plan.allowedSurfaces[surface]
}

const slugify = (value: string): string => (
    value
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
)

const isAbsoluteUrl = (value?: string): boolean => {
    if (value == null || value.trim() === '') return false
    try {
        const url = new URL(value)
        return url.protocol === 'http:' || url.protocol === 'https:'
    } catch {
        return false
    }
}

const getDomainLabel = (value?: string): string => {
    if (value == null || value.trim() === '') return 'No website'
    try {
        return new URL(value).hostname.replace(/^www\./, '')
    } catch {
        return value
    }
}

const getPartnerInitials = (partner: Pick<SponsoredPartner, 'partnerName' | 'id'>): string => {
    const source = partner.partnerName.trim() !== '' ? partner.partnerName : partner.id
    const initials = source
        .split(/[\s-]+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0]?.toUpperCase())
        .join('')
    return initials || 'SP'
}

const optionalString = (value?: string): string | undefined => {
    const trimmed = value?.trim()
    return trimmed != null && trimmed.length > 0 ? trimmed : undefined
}

const optionalNumber = (value?: number): number | undefined => (
    typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : undefined
)

const calculateStatus = (partner: SponsoredPartner, now = new Date()): SponsoredPartnerCalculatedStatus => {
    if (!partner.enabled) return 'Disabled'

    if (partner.activeFrom != null && partner.activeFrom.trim() !== '') {
        const activeFrom = new Date(`${partner.activeFrom}T00:00:00`)
        const todayEnd = new Date(now)
        todayEnd.setHours(23, 59, 59, 999)
        if (Number.isFinite(activeFrom.getTime()) && activeFrom > todayEnd) return 'Scheduled'
    }

    if (partner.activeUntil != null && partner.activeUntil.trim() !== '') {
        const activeUntil = new Date(`${partner.activeUntil}T23:59:59`)
        const todayStart = new Date(now)
        todayStart.setHours(0, 0, 0, 0)
        if (Number.isFinite(activeUntil.getTime()) && activeUntil < todayStart) return 'Expired'
    }

    if (partner.previewOnly) return 'Dev Preview'

    return 'Live'
}

const getExpiryLabel = (partner: SponsoredPartner, now = new Date()): string => {
    if (partner.activeUntil == null || partner.activeUntil.trim() === '') return 'No end date'

    const activeUntil = new Date(`${partner.activeUntil}T00:00:00`)
    if (!Number.isFinite(activeUntil.getTime())) return 'No end date'

    const todayStart = new Date(now)
    todayStart.setHours(0, 0, 0, 0)
    activeUntil.setHours(0, 0, 0, 0)

    const days = Math.ceil((activeUntil.getTime() - todayStart.getTime()) / 86400000)
    if (days < 0) return 'Expired'
    if (days === 0) return 'Expires today'
    if (days === 1) return 'Expires in 1 day'
    return `Expires in ${days} days`
}

const isProductionActivePartner = (partner: SponsoredPartner, now = new Date()): boolean => (
    calculateStatus(partner, now) === 'Live' && partner.previewOnly !== true
)

const getPlanUsage = (
    partners: SponsoredPartner[],
    planId: SponsoredPartnerPlanId,
    editingOriginalId?: string | null,
    candidate?: SponsoredPartner
): number => {
    const current = partners.filter((partner) => (
        partner.id !== editingOriginalId &&
        partner.planId === planId &&
        isProductionActivePartner(partner)
    )).length

    return current + (
        candidate != null &&
        candidate.planId === planId &&
        isProductionActivePartner(candidate)
            ? 1
            : 0
    )
}

const rangesOverlap = (aFrom?: string, aUntil?: string, bFrom?: string, bUntil?: string): boolean => {
    const min = -8640000000000000
    const max = 8640000000000000
    const toStart = (value?: string): number => value != null && value !== '' ? new Date(`${value}T00:00:00`).getTime() : min
    const toEnd = (value?: string): number => value != null && value !== '' ? new Date(`${value}T23:59:59`).getTime() : max
    return toStart(aFrom) <= toEnd(bUntil) && toStart(bFrom) <= toEnd(aUntil)
}

const hasActivePlatinumOverlap = (
    partners: SponsoredPartner[],
    candidate: SponsoredPartner,
    editingOriginalId: string | null
): boolean => (
    candidate.planId === 'platinum' &&
    candidate.enabled === true &&
    candidate.previewOnly !== true &&
    partners.some((partner) => (
        partner.id !== editingOriginalId &&
        partner.planId === 'platinum' &&
        partner.enabled === true &&
        partner.previewOnly !== true &&
        calculateStatus(partner) !== 'Expired' &&
        rangesOverlap(partner.activeFrom, partner.activeUntil, candidate.activeFrom, candidate.activeUntil)
    ))
)

const statusClass = (status: SponsoredPartnerCalculatedStatus): string => (
    status === 'Live'
        ? 'live'
        : status === 'Scheduled'
            ? 'scheduled'
            : status === 'Dev Preview'
                ? 'preview'
                : status === 'Expired'
                    ? 'expired'
                    : 'disabled'
)

const expiryClass = (label: string): string => (
    label === 'Expired'
        ? 'expired'
        : label === 'Expires today'
            ? 'scheduled'
            : label === 'No end date'
                ? 'neutral'
                : 'live'
)

const createDefaultSurface = (surface: SurfaceKey, partner: SponsoredPartner): SponsoredPartner['surfaces'][SurfaceKey] => {
    switch (surface) {
        case 'exploreHero':
            return {
                enabled: true,
                imageUrl: '',
                title: partner.partnerName,
                description: '',
                autoSlideMs: 4500
            }
        case 'unitsCatalogueInline':
        case 'profileListingsInline':
            return {
                enabled: true,
                imageUrl: '',
                alt: partner.partnerName,
                title: partner.partnerName,
                description: '',
                variant: 'yellow-curve',
                insertEvery: 3,
                startAfter: 2,
                autoSlideMs: 4500
            }
        case 'modalCard':
            return {
                enabled: true,
                surfaces: ['unit', 'viewing', 'offer', 'advertise'],
                title: partner.partnerName,
                description: ''
            }
        case 'sponsoredChat':
            return {
                enabled: true,
                title: partner.partnerName,
                listPreview: '',
                messages: [blankChatMessage()]
            }
    }
}

const blankChatMessage = (): SponsoredPartnerChatMessage => ({
    id: slugify(nanoid(8)),
    messageTitle: '',
    messageBody: ''
})

const updateSurface = <K extends SurfaceKey>(
    partner: SponsoredPartner,
    surface: K,
    patch: Partial<NonNullable<SponsoredPartner['surfaces'][K]>>
): SponsoredPartner => ({
    ...partner,
    surfaces: {
        ...partner.surfaces,
        [surface]: {
            ...(partner.surfaces[surface] ?? createDefaultSurface(surface, partner)),
            ...patch
        }
    }
})

const applyPlanRestrictions = (partner: SponsoredPartner): SponsoredPartner => {
    const plan = getPlan(partner.planId)
    if (plan == null) return partner

    return SURFACE_KEYS.reduce((current, surfaceKey) => {
        const surface = current.surfaces[surfaceKey]
        if (surface == null || plan.allowedSurfaces[surfaceKey]) return current
        return updateSurface(current, surfaceKey, { enabled: false } as Partial<NonNullable<SponsoredPartner['surfaces'][SurfaceKey]>>)
    }, partner)
}

const normalizePartnerForSave = (partner: SponsoredPartner): SponsoredPartner => {
    const surfaces: SponsoredPartner['surfaces'] = {}
    const exploreHero = partner.surfaces.exploreHero
    const unitsCatalogueInline = partner.surfaces.unitsCatalogueInline
    const profileListingsInline = partner.surfaces.profileListingsInline
    const modalCard = partner.surfaces.modalCard
    const sponsoredChat = partner.surfaces.sponsoredChat
    const plan = getPlan(partner.planId)
    const isAllowed = (surface: SurfaceKey): boolean => plan?.allowedSurfaces[surface] === true

    if (exploreHero != null) {
        const enabled = exploreHero.enabled === true && isAllowed('exploreHero')

        surfaces.exploreHero = {
            enabled,
            imageUrl: exploreHero.imageUrl.trim(),
            title: exploreHero.title.trim(),
            description: exploreHero.description.trim(),
            ...(optionalString(exploreHero.linkLabel) != null ? { linkLabel: optionalString(exploreHero.linkLabel) } : {}),
            ...(optionalString(exploreHero.ctaText) != null ? { ctaText: optionalString(exploreHero.ctaText) } : {}),
            ...(optionalNumber(exploreHero.autoSlideMs) != null ? { autoSlideMs: optionalNumber(exploreHero.autoSlideMs) } : {})
        }
    }

    if (unitsCatalogueInline != null) {
        surfaces.unitsCatalogueInline = normalizeInlineSurface(unitsCatalogueInline, unitsCatalogueInline.enabled === true && isAllowed('unitsCatalogueInline'))
    }

    if (profileListingsInline != null) {
        surfaces.profileListingsInline = normalizeInlineSurface(profileListingsInline, profileListingsInline.enabled === true && isAllowed('profileListingsInline'))
    }

    if (modalCard != null) {
        surfaces.modalCard = {
            enabled: modalCard.enabled === true && isAllowed('modalCard'),
            surfaces: modalCard.surfaces,
            title: modalCard.title.trim(),
            description: modalCard.description.trim(),
            ...(optionalString(modalCard.linkLabel) != null ? { linkLabel: optionalString(modalCard.linkLabel) } : {})
        }
    }

    if (sponsoredChat != null) {
        surfaces.sponsoredChat = {
            enabled: sponsoredChat.enabled === true && isAllowed('sponsoredChat'),
            ...(optionalString(sponsoredChat.avatarUrl) != null ? { avatarUrl: optionalString(sponsoredChat.avatarUrl) } : {}),
            title: sponsoredChat.title.trim(),
            listPreview: sponsoredChat.listPreview.trim(),
            ...(optionalString(sponsoredChat.ctaText) != null ? { ctaText: optionalString(sponsoredChat.ctaText) } : {}),
            messages: (sponsoredChat.messages ?? []).map((message) => ({
                id: message.id.trim(),
                messageTitle: message.messageTitle.trim(),
                messageBody: message.messageBody.trim(),
                ...(optionalString(message.imageUrl) != null ? { imageUrl: optionalString(message.imageUrl) } : {}),
                ...(optionalString(message.cardTitle) != null ? { cardTitle: optionalString(message.cardTitle) } : {}),
                ...(optionalString(message.cardDescription) != null ? { cardDescription: optionalString(message.cardDescription) } : {}),
                ...(optionalString(message.linkLabel) != null ? { linkLabel: optionalString(message.linkLabel) } : {}),
                ...(optionalString(message.ctaText) != null ? { ctaText: optionalString(message.ctaText) } : {})
            }))
        }
    }

    return {
        id: partner.id.trim(),
        partnerName: partner.partnerName.trim(),
        ...(partner.planId != null ? { planId: partner.planId } : {}),
        enabled: partner.enabled,
        previewOnly: partner.previewOnly,
        ...(optionalString(partner.activeFrom) != null ? { activeFrom: optionalString(partner.activeFrom) } : {}),
        ...(optionalString(partner.activeUntil) != null ? { activeUntil: optionalString(partner.activeUntil) } : {}),
        websiteUrl: partner.websiteUrl.trim(),
        ...(optionalString(partner.logoUrl) != null ? { logoUrl: optionalString(partner.logoUrl) } : {}),
        surfaces
    }
}

const normalizeInlineSurface = (surface: NonNullable<SponsoredPartner['surfaces']['unitsCatalogueInline']>, enabled: boolean) => ({
    enabled,
    imageUrl: surface.imageUrl.trim(),
    alt: surface.alt.trim(),
    ...(optionalString(surface.title) != null ? { title: optionalString(surface.title) } : {}),
    ...(optionalString(surface.description) != null ? { description: optionalString(surface.description) } : {}),
    ...(surface.variant != null ? { variant: surface.variant } : {}),
    ...(optionalNumber(surface.insertEvery) != null ? { insertEvery: optionalNumber(surface.insertEvery) } : {}),
    ...(optionalNumber(surface.startAfter) != null ? { startAfter: optionalNumber(surface.startAfter) } : {}),
    ...(optionalNumber(surface.autoSlideMs) != null ? { autoSlideMs: optionalNumber(surface.autoSlideMs) } : {})
})

const validatePartner = (partner: SponsoredPartner, allPartners: SponsoredPartner[], originalId: string | null): string[] => {
    const errors: string[] = []
    const id = partner.id.trim()
    const plan = getPlan(partner.planId)

    if (partner.partnerName.trim() === '') errors.push('Partner name is required.')
    if (id === '') errors.push('Partner ID is required.')
    if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(id)) errors.push('Partner ID must be a lowercase slug.')
    if (allPartners.some((item) => item.id === id && item.id !== originalId)) errors.push('Partner ID must be unique.')
    if (partner.enabled && partner.planId == null) errors.push('A plan is required before enabling a partner.')
    if (!isAbsoluteUrl(partner.websiteUrl)) errors.push('Website URL must be a valid absolute URL.')
    if (partner.logoUrl != null && partner.logoUrl.trim() !== '' && !isAbsoluteUrl(partner.logoUrl)) errors.push('Logo URL must be a valid absolute URL.')

    if (partner.activeFrom != null && partner.activeUntil != null && partner.activeFrom !== '' && partner.activeUntil !== '') {
        const from = new Date(`${partner.activeFrom}T00:00:00`)
        const until = new Date(`${partner.activeUntil}T00:00:00`)
        if (Number.isFinite(from.getTime()) && Number.isFinite(until.getTime()) && until < from) {
            errors.push('Active until cannot be earlier than active from.')
        }
    }

    if (plan != null) {
        SURFACE_KEYS.forEach((surfaceKey) => {
            if (partner.surfaces[surfaceKey]?.enabled === true && !plan.allowedSurfaces[surfaceKey]) {
                errors.push(`${SURFACE_SHORT_LABELS[surfaceKey]} is not included in ${plan.title}.`)
            }
        })
    }

    if (hasActivePlatinumOverlap(allPartners, partner, originalId)) {
        errors.push('Platinum Plan allows only one production active ad for overlapping active dates.')
    }

    validateExploreHero(partner, errors)
    validateInline(partner, 'unitsCatalogueInline', 'Units Catalogue Inline', errors)
    validateInline(partner, 'profileListingsInline', 'Profile Listings Inline', errors)
    validateModalCard(partner, errors)
    validateSponsoredChat(partner, errors)

    return errors
}

const validateExploreHero = (partner: SponsoredPartner, errors: string[]): void => {
    const surface = partner.surfaces.exploreHero
    if (surface?.enabled !== true) return
    if (!isAbsoluteUrl(surface.imageUrl)) errors.push('Explore Hero image URL must be a valid absolute URL.')
    if (surface.title.trim() === '') errors.push('Explore Hero title is required.')
    if (surface.description.trim() === '') errors.push('Explore Hero description is required.')
}

const validateInline = (
    partner: SponsoredPartner,
    surfaceKey: 'unitsCatalogueInline' | 'profileListingsInline',
    label: string,
    errors: string[]
): void => {
    const surface = partner.surfaces[surfaceKey]
    if (surface?.enabled !== true) return
    if (!isAbsoluteUrl(surface.imageUrl)) errors.push(`${label} image URL must be a valid absolute URL.`)
    if (surface.alt.trim() === '') errors.push(`${label} alt text is required.`)
    if (surface.variant != null && !INLINE_VARIANTS.some((variant) => variant.value === surface.variant)) {
        errors.push(`${label} variant is not supported.`)
    }
}

const validateModalCard = (partner: SponsoredPartner, errors: string[]): void => {
    const surface = partner.surfaces.modalCard
    if (surface?.enabled !== true) return
    if (surface.title.trim() === '') errors.push('Modal Card title is required.')
    if (surface.description.trim() === '') errors.push('Modal Card description is required.')
    if (!Array.isArray(surface.surfaces) || surface.surfaces.length === 0) errors.push('Modal Card must target at least one modal surface.')
    if (surface.surfaces.some((item) => !MODAL_SURFACES.some((surfaceOption) => surfaceOption.value === item))) {
        errors.push('Modal Card surfaces must use only unit, viewing, offer, or advertise.')
    }
}

const validateSponsoredChat = (partner: SponsoredPartner, errors: string[]): void => {
    const surface = partner.surfaces.sponsoredChat
    if (surface?.enabled !== true) return
    if (surface.title.trim() === '') errors.push('Sponsored Chat title is required.')
    if (surface.listPreview.trim() === '') errors.push('Sponsored Chat list preview is required.')
    if (surface.avatarUrl != null && surface.avatarUrl.trim() !== '' && !isAbsoluteUrl(surface.avatarUrl)) {
        errors.push('Sponsored Chat avatar URL must be a valid absolute URL.')
    }

    const messages = surface.messages ?? []
    if (messages.length === 0) errors.push('Sponsored Chat must contain at least one message.')
    const messageIds = messages.map((message) => message.id.trim())
    if (messageIds.some((id) => id === '')) errors.push('Sponsored Chat message IDs are required.')
    if (new Set(messageIds).size !== messageIds.length) errors.push('Sponsored Chat message IDs must be stable and unique.')

    messages.forEach((message, index) => {
        if (message.messageTitle.trim() === '') errors.push(`Sponsored Chat message ${index + 1} title is required.`)
        if (message.messageBody.trim() === '') errors.push(`Sponsored Chat message ${index + 1} body is required.`)
        if (message.imageUrl != null && message.imageUrl.trim() !== '' && !isAbsoluteUrl(message.imageUrl)) {
            errors.push(`Sponsored Chat message ${index + 1} image URL must be a valid absolute URL.`)
        }
    })
}

export default function SponsoredPartnersAdminPage(): ReactElement {
    const [remoteConfig, setRemoteConfig] = useState<SponsoredPartnersRemoteConfig>(DEFAULT_SPONSORED_PARTNERS_CONFIG)
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [uploadingByKey, setUploadingByKey] = useState<UploadState>({})
    const [uploadErrors, setUploadErrors] = useState<UploadErrors>({})
    const [formPartner, setFormPartner] = useState<SponsoredPartner>(blankPartner())
    const [editorMode, setEditorMode] = useState<EditorMode>('idle')
    const [editingOriginalId, setEditingOriginalId] = useState<string | null>(null)
    const [idEdited, setIdEdited] = useState(false)
    const [validationErrors, setValidationErrors] = useState<string[]>([])
    const [lastRequestPayload, setLastRequestPayload] = useState<unknown>(null)
    const [searchTerm, setSearchTerm] = useState('')

    useEffect(() => {
        void loadConfig()
    }, [])

    const partners = remoteConfig.partners
    const sortedPartners = useMemo(() => [...partners].sort((a, b) => a.partnerName.localeCompare(b.partnerName)), [partners])
    const filteredPartners = useMemo(() => {
        const query = searchTerm.trim().toLowerCase()
        if (query === '') return sortedPartners
        return sortedPartners.filter((partner) => (
            partner.partnerName.toLowerCase().includes(query) ||
            partner.id.toLowerCase().includes(query) ||
            getDomainLabel(partner.websiteUrl).toLowerCase().includes(query)
        ))
    }, [searchTerm, sortedPartners])

    const loadConfig = async (): Promise<void> => {
        setLoading(true)
        try {
            const config = await fetchSponsoredPartnersConfig()
            setRemoteConfig(config)
        } catch (error: any) {
            await swalError(error?.message ?? 'Could not load Sponsored Partners.')
        } finally {
            setLoading(false)
        }
    }

    const startCreate = (): void => {
        setFormPartner(blankPartner())
        setEditorMode('create')
        setEditingOriginalId(null)
        setIdEdited(false)
        setValidationErrors([])
    }

    const startEdit = (partner: SponsoredPartner): void => {
        setFormPartner(JSON.parse(JSON.stringify(partner)) as SponsoredPartner)
        setEditorMode('edit')
        setEditingOriginalId(partner.id)
        setIdEdited(true)
        setValidationErrors([])
    }

    const closeEditor = (): void => {
        setFormPartner(blankPartner())
        setEditorMode('idle')
        setEditingOriginalId(null)
        setIdEdited(false)
        setValidationErrors([])
    }

    const duplicatePartner = (partner: SponsoredPartner): void => {
        const copy = JSON.parse(JSON.stringify(partner)) as SponsoredPartner
        const nextId = buildDuplicateId(partner.id, partners)
        setFormPartner({
            ...copy,
            id: nextId,
            partnerName: `${copy.partnerName} Copy`,
            enabled: false,
            previewOnly: true
        })
        setEditorMode('create')
        setEditingOriginalId(null)
        setIdEdited(true)
        setValidationErrors([])
    }

    const deletePartner = async (partner: SponsoredPartner): Promise<void> => {
        const result = await swalSureDelete()
        if (!result.isConfirmed) return

        const nextPartners = partners.filter((item) => item.id !== partner.id)
        try {
            setSaving(true)
            const saveResult = await saveSponsoredPartnersConfig(nextPartners)
            setRemoteConfig(saveResult.config)
            setLastRequestPayload(saveResult.requestPayload)
            if (editingOriginalId === partner.id) closeEditor()
            await swalSuccess('Sponsored partner deleted successfully.')
        } catch (error: any) {
            await swalError(error?.message ?? 'Could not delete sponsored partner.')
        } finally {
            setSaving(false)
        }
    }

    const buildDuplicateId = (id: string, list: SponsoredPartner[]): string => {
        let index = 1
        let candidate = `${id}-copy`
        while (list.some((partner) => partner.id === candidate)) {
            index += 1
            candidate = `${id}-copy-${index}`
        }
        return candidate
    }

    const savePartner = async (): Promise<void> => {
        const normalized = normalizePartnerForSave(formPartner)
        const errors = validatePartner(normalized, partners, editingOriginalId)
        setValidationErrors(errors)
        if (errors.length > 0) return

        const nextPartners = editingOriginalId == null
            ? [...partners, normalized]
            : partners.map((partner) => partner.id === editingOriginalId ? normalized : partner)
        const isCreate = editingOriginalId == null

        try {
            setSaving(true)
            const saveResult = await saveSponsoredPartnersConfig(nextPartners)
            setRemoteConfig(saveResult.config)
            setLastRequestPayload(saveResult.requestPayload)
            setEditorMode('edit')
            setEditingOriginalId(normalized.id)
            setFormPartner(normalized)
            setIdEdited(true)
            await swalSuccess(isCreate ? 'Sponsored partner created successfully.' : 'Sponsored partner updated successfully.')
        } catch (error: any) {
            await swalError(error?.message ?? 'Could not save Sponsored Partners.')
        } finally {
            setSaving(false)
        }
    }

    const updatePartnerField = <K extends keyof SponsoredPartner>(field: K, value: SponsoredPartner[K]): void => {
        setFormPartner((current) => {
            const next = { ...current, [field]: value }
            if (field === 'partnerName' && !idEdited) {
                next.id = slugify(String(value))
            }
            return next
        })
    }

    const updatePlan = (planId?: SponsoredPartnerPlanId): void => {
        setFormPartner((current) => applyPlanRestrictions({
            ...current,
            ...(planId != null ? { planId } : { planId: undefined })
        }))
    }

    const toggleSurface = (surface: SurfaceKey, enabled: boolean): void => {
        setFormPartner((current) => {
            if (enabled && !isSurfaceAllowedForPlan(current, surface)) return current
            if (enabled) {
                return updateSurface(current, surface, { enabled: true } as Partial<NonNullable<SponsoredPartner['surfaces'][SurfaceKey]>>)
            }

            return updateSurface(current, surface, { enabled: false } as Partial<NonNullable<SponsoredPartner['surfaces'][SurfaceKey]>>)
        })
    }

    const uploadImageForField = async (key: string, file: File, onUrl: (url: string) => void): Promise<void> => {
        setUploadingByKey((current) => ({ ...current, [key]: true }))
        setUploadErrors((current) => ({ ...current, [key]: undefined }))
        try {
            const url = await uploadSponsoredPartnerImage(file)
            onUrl(url)
        } catch (error: any) {
            const message = error?.message ?? 'Could not upload image.'
            setUploadErrors((current) => ({ ...current, [key]: message }))
            await swalError(message)
        } finally {
            setUploadingByKey((current) => ({ ...current, [key]: false }))
        }
    }

    const isEditorOpen = editorMode !== 'idle'
    const previewStatus = calculateStatus(formPartner)
    const previewExpiry = getExpiryLabel(formPartner)
    const selectedPlan = getPlan(formPartner.planId)
    const planUsages = useMemo(() => ({
        silver: getPlanUsage(partners, 'silver', editingOriginalId, formPartner),
        gold: getPlanUsage(partners, 'gold', editingOriginalId, formPartner),
        platinum: getPlanUsage(partners, 'platinum', editingOriginalId, formPartner)
    }), [editingOriginalId, formPartner, partners])
    const currentPlanUsage = formPartner.planId != null ? getPlanUsage(partners, formPartner.planId, editingOriginalId, formPartner) : null
    const lockedSurfaceNames = selectedPlan == null
        ? []
        : SURFACE_KEYS.filter((surfaceKey) => !selectedPlan.allowedSurfaces[surfaceKey] && formPartner.surfaces[surfaceKey] != null).map((surfaceKey) => SURFACE_SHORT_LABELS[surfaceKey])
    const hasAnyUpload = Object.values(uploadingByKey).some(Boolean)

    return (
        <div className="sponsored-partners-admin">
            <div className="sponsored-partners-admin__inner">
                <header className="sponsored-partners-admin__header">
                    <div>
                        <h1 className="sponsored-partners-admin__title">Sponsored Partners</h1>
                        <p className="sponsored-partners-admin__subtitle">Stored in Config.SponsoredPartners. Other Config fields are not included in save payloads.</p>
                    </div>
                    <div className="d-flex gap-2 flex-wrap">
                        <button className="btn btn-light btn-sm" type="button" onClick={loadConfig} disabled={loading || saving}>Reload</button>
                        <button className="btn btn-bso btn-sm" type="button" onClick={startCreate}>New partner</button>
                    </div>
                </header>

                <div className="sponsored-partners-admin__grid">
                    <section className="sponsored-partners-admin__panel">
                        <div className="sponsored-partners-admin__panel-head">
                            <div>
                                <h2 className="sponsored-partners-admin__section-title m-0">Partners</h2>
                                <span className="sponsored-partners-admin__panel-subtitle">{partners.length} campaigns</span>
                            </div>
                            <button className="btn btn-bso btn-sm" type="button" onClick={startCreate}>Create</button>
                        </div>
                        <div className="sponsored-partners-admin__search">
                            <span className="sponsored-partners-admin__search-icon">⌕</span>
                            <input
                                value={searchTerm}
                                placeholder="Search partners"
                                onChange={(event) => { setSearchTerm(event.target.value) }}
                            />
                        </div>

                        {loading
                            ? <div className="spinner-border spinner-border-sm" />
                            : <div className="sponsored-partners-admin__partner-list">
                                {filteredPartners.length === 0
                                    ? <div className="sponsored-partners-admin__empty-list">{partners.length === 0 ? 'No sponsored partners configured.' : 'No partners match this search.'}</div>
                                    : filteredPartners.map((partner) => {
                                        const status = calculateStatus(partner)
                                        const expires = getExpiryLabel(partner)
                                        const plan = getPlan(partner.planId)
                                        const usage = partner.planId != null ? getPlanUsage(partners, partner.planId) : null
                                        const limitReached = plan != null && usage != null && usage >= plan.activeAdsLimit
                                        const selected = editorMode === 'edit' && editingOriginalId === partner.id

                                        return (
                                            <div
                                                key={partner.id}
                                                role="button"
                                                tabIndex={0}
                                                className={`sponsored-partners-admin__partner-item${selected ? ' sponsored-partners-admin__partner-item--selected' : ''}`}
                                                onClick={() => { startEdit(partner) }}
                                                onKeyDown={(event) => {
                                                    if (event.key === 'Enter' || event.key === ' ') {
                                                        event.preventDefault()
                                                        startEdit(partner)
                                                    }
                                                }}
                                            >
                                                <span className="sponsored-partners-admin__avatar-cell">
                                                    <PartnerAvatar partner={partner} size="sm" />
                                                </span>
                                                <span className="sponsored-partners-admin__partner-main">
                                                    <strong>{partner.partnerName || partner.id}</strong>
                                                    <span className="sponsored-partners-admin__id">{partner.id}</span>
                                                </span>
                                                <span className="sponsored-partners-admin__partner-meta">
                                                    <span>{getPlanShortLabel(partner.planId)}</span>
                                                    <span>{getPlanPriceLabel(partner.planId)}</span>
                                                    <span>{getDomainLabel(partner.websiteUrl)}</span>
                                                </span>
                                                <span className="sponsored-partners-admin__badge-row">
                                                    <span className={`sponsored-partners-admin__status sponsored-partners-admin__status--${statusClass(status)}`}>{status}</span>
                                                    <span className={`sponsored-partners-admin__status sponsored-partners-admin__status--${expiryClass(expires)}`}>{expires}</span>
                                                    {plan != null && usage != null
                                                        ? <span className={`sponsored-partners-admin__status${limitReached ? ' sponsored-partners-admin__status--expired' : ''}`}>{usage} / {plan.activeAdsLimit}</span>
                                                        : null}
                                                </span>
                                                <span className="sponsored-partners-admin__actions sponsored-partners-admin__actions--list" onClick={(event) => { event.stopPropagation() }}>
                                                    <button className="btn btn-light-primary btn-sm" type="button" onClick={() => { duplicatePartner(partner) }}>Duplicate</button>
                                                    <button className="btn btn-light-danger btn-sm" type="button" onClick={() => { void deletePartner(partner) }}>Delete</button>
                                                </span>
                                            </div>
                                        )
                                    })}
                            </div>}
                    </section>

                    <aside className="sponsored-partners-admin__panel">
                        {!isEditorOpen
                            ? <div className="sponsored-partners-admin__empty-state">
                                <div className="sponsored-partners-admin__empty-icon">SP</div>
                                <h2 className="sponsored-partners-admin__section-title m-0">Select a partner</h2>
                                <p>Choose a partner from the list or create a new sponsored campaign.</p>
                                <button className="btn btn-bso btn-sm" type="button" onClick={startCreate}>Create partner</button>
                                <span className="sponsored-partners-admin__empty-hint">Preview-only campaigns stay hidden from production.</span>
                            </div>
                            : <>
                                <div className="sponsored-partners-admin__editor-header">
                                    <PartnerAvatar partner={formPartner} size="lg" />
                                    <div>
                                        <div className="sponsored-partners-admin__eyebrow">{editorMode === 'create' ? 'New campaign' : 'Selected campaign'}</div>
                                        <h2 className="sponsored-partners-admin__editor-title">{formPartner.partnerName || (editorMode === 'create' ? 'Untitled partner' : formPartner.id)}</h2>
                                        <div className="sponsored-partners-admin__editor-domain">{getDomainLabel(formPartner.websiteUrl)}</div>
                                        <div className="sponsored-partners-admin__badge-row mt-2">
                                            <span className="sponsored-partners-admin__status sponsored-partners-admin__status--neutral">{getPlanShortLabel(formPartner.planId)}</span>
                                            <span className={`sponsored-partners-admin__status sponsored-partners-admin__status--${statusClass(previewStatus)}`}>{previewStatus}</span>
                                            <span className={`sponsored-partners-admin__status sponsored-partners-admin__status--${expiryClass(previewExpiry)}`}>{previewExpiry}</span>
                                            <span className={`sponsored-partners-admin__status sponsored-partners-admin__status--${formPartner.enabled ? 'live' : 'neutral'}`}>{formPartner.enabled ? 'Enabled' : 'Hidden'}</span>
                                            {formPartner.previewOnly ? <span className="sponsored-partners-admin__status sponsored-partners-admin__status--preview">Preview only</span> : null}
                                        </div>
                                    </div>
                                    <div className="sponsored-partners-admin__editor-actions">
                                        <button className="btn btn-light btn-sm" type="button" onClick={closeEditor}>Close</button>
                                    </div>
                                </div>

                                {validationErrors.length > 0
                                    ? <div className="sponsored-partners-admin__errors mb-3">
                                        {validationErrors.map((error) => <div key={error}>{error}</div>)}
                                    </div>
                                    : null}

                                <div className="sponsored-partners-admin__form">
                                    <FormSection title="Campaign details">
                            <div className="sponsored-partners-admin__field">
                                <label>Partner name</label>
                                <input value={formPartner.partnerName} onChange={(event) => { updatePartnerField('partnerName', event.target.value) }} />
                            </div>
                            <div className="sponsored-partners-admin__field">
                                <label>ID / slug</label>
                                <input
                                    value={formPartner.id}
                                    onChange={(event) => {
                                        setIdEdited(true)
                                        updatePartnerField('id', slugify(event.target.value))
                                    }}
                                />
                            </div>
                            <div className="sponsored-partners-admin__field">
                                <label>Website URL</label>
                                <input value={formPartner.websiteUrl} onChange={(event) => { updatePartnerField('websiteUrl', event.target.value) }} placeholder="https://example.com" />
                            </div>
                                    </FormSection>

                                    <FormSection title="Plan">
                            <PlanSelector
                                value={formPartner.planId}
                                usages={planUsages}
                                onChange={updatePlan}
                            />
                            {selectedPlan != null
                                ? <div className="sponsored-partners-admin__plan-details">
                                    <div>
                                        <strong>{selectedPlan.title}</strong>
                                        <span>{selectedPlan.priceLabel} - {selectedPlan.commitmentTerms}</span>
                                    </div>
                                    <span>Includes: {getPlanIncludedSurfaceNames(selectedPlan)}</span>
                                    <span>{selectedPlan.networkLimitLabel}</span>
                                    {selectedPlan.sponsoredChatLimitLabel != null ? <span>Sponsored Chat: {selectedPlan.sponsoredChatLimitLabel}</span> : null}
                                    {selectedPlan.exploreHeroLimitLabel != null ? <span>Explore Hero: {selectedPlan.exploreHeroLimitLabel}</span> : null}
                                    {currentPlanUsage != null ? <span>Current production usage: {currentPlanUsage} / {selectedPlan.activeAdsLimit}</span> : null}
                                </div>
                                : <div className="sponsored-partners-admin__help">No plan selected / Legacy. Choose a plan before enabling this partner.</div>}
                                    </FormSection>

                                    <FormSection title="Schedule & visibility">
                            <div className="sponsored-partners-admin__row">
                                <div className="sponsored-partners-admin__field">
                                    <label>Active from</label>
                                    <input type="date" value={formPartner.activeFrom ?? ''} onChange={(event) => { updatePartnerField('activeFrom', event.target.value) }} />
                                </div>
                                <div className="sponsored-partners-admin__field">
                                    <label>Active until</label>
                                    <input type="date" value={formPartner.activeUntil ?? ''} onChange={(event) => { updatePartnerField('activeUntil', event.target.value) }} />
                                </div>
                            </div>
                            <div className="sponsored-partners-admin__visibility-grid">
                            <label className="sponsored-partners-admin__check sponsored-partners-admin__check-card">
                                <input type="checkbox" checked={formPartner.enabled} onChange={(event) => { updatePartnerField('enabled', event.target.checked) }} />
                                <span>
                                    Enabled
                                    <div className="sponsored-partners-admin__help">Campaign can render when dates and placement settings allow it.</div>
                                </span>
                            </label>
                            <label className="sponsored-partners-admin__check sponsored-partners-admin__check-card">
                                <input type="checkbox" checked={formPartner.previewOnly} onChange={(event) => { updatePartnerField('previewOnly', event.target.checked) }} />
                                <span>
                                    Preview only
                                    <div className="sponsored-partners-admin__help">Visible only on localhost and the development website when Enabled is on. Hidden from production users.</div>
                                </span>
                            </label>
                            </div>
                                    </FormSection>

                                    <FormSection title="Assets">
                            <ImageUrlField
                                fieldKey="logo"
                                label="Logo URL"
                                value={formPartner.logoUrl ?? ''}
                                uploading={uploadingByKey.logo === true}
                                error={uploadErrors.logo}
                                onChange={(url) => { updatePartnerField('logoUrl', url) }}
                                onUploadFile={(file) => uploadImageForField('logo', file, (url) => { updatePartnerField('logoUrl', url) })}
                                helperText="Logo source image: square icon/mark, recommended 128×128 or 256×256 px. Displayed in the app around 24–44 px depending on placement. Use a compact brand icon, not a wide horizontal wordmark. Transparent PNG/WebP/SVG preferred."
                                preview={<LogoPreview value={formPartner.logoUrl} />}
                            />
                                    </FormSection>

                                    <FormSection title="Placements / surfaces">
                            <PlanSurfaceSummary planId={formPartner.planId} />
                            {lockedSurfaceNames.length > 0
                                ? <div className="sponsored-partners-admin__warning">These placements are not included in the selected plan and will be saved as disabled: {lockedSurfaceNames.join(', ')}.</div>
                                : null}
                            <SurfaceToggle surfaceKey="exploreHero" enabled={formPartner.surfaces.exploreHero?.enabled === true} locked={!isSurfaceAllowedForPlan(formPartner, 'exploreHero')} onChange={(enabled) => { toggleSurface('exploreHero', enabled) }}>
                                <ExploreHeroFields partner={formPartner} setPartner={setFormPartner} uploadImageForField={uploadImageForField} uploadingByKey={uploadingByKey} uploadErrors={uploadErrors} />
                            </SurfaceToggle>
                            <SurfaceToggle surfaceKey="unitsCatalogueInline" enabled={formPartner.surfaces.unitsCatalogueInline?.enabled === true} locked={!isSurfaceAllowedForPlan(formPartner, 'unitsCatalogueInline')} onChange={(enabled) => { toggleSurface('unitsCatalogueInline', enabled) }}>
                                <InlineFields surfaceKey="unitsCatalogueInline" partner={formPartner} setPartner={setFormPartner} uploadImageForField={uploadImageForField} uploadingByKey={uploadingByKey} uploadErrors={uploadErrors} />
                            </SurfaceToggle>
                            <SurfaceToggle surfaceKey="profileListingsInline" enabled={formPartner.surfaces.profileListingsInline?.enabled === true} locked={!isSurfaceAllowedForPlan(formPartner, 'profileListingsInline')} onChange={(enabled) => { toggleSurface('profileListingsInline', enabled) }}>
                                <InlineFields surfaceKey="profileListingsInline" partner={formPartner} setPartner={setFormPartner} uploadImageForField={uploadImageForField} uploadingByKey={uploadingByKey} uploadErrors={uploadErrors} />
                            </SurfaceToggle>
                            <SurfaceToggle surfaceKey="modalCard" enabled={formPartner.surfaces.modalCard?.enabled === true} locked={!isSurfaceAllowedForPlan(formPartner, 'modalCard')} onChange={(enabled) => { toggleSurface('modalCard', enabled) }}>
                                <ModalCardFields partner={formPartner} setPartner={setFormPartner} />
                            </SurfaceToggle>
                            <SurfaceToggle surfaceKey="sponsoredChat" enabled={formPartner.surfaces.sponsoredChat?.enabled === true} locked={!isSurfaceAllowedForPlan(formPartner, 'sponsoredChat')} onChange={(enabled) => { toggleSurface('sponsoredChat', enabled) }}>
                                <SponsoredChatFields partner={formPartner} setPartner={setFormPartner} uploadImageForField={uploadImageForField} uploadingByKey={uploadingByKey} uploadErrors={uploadErrors} />
                            </SurfaceToggle>

                                    </FormSection>

                                    <FormSection title="Preview">
                            <PartnerPreviews partner={formPartner} />
                                    </FormSection>

                            {lastRequestPayload != null
                                ? <details>
                                    <summary className="text-gray-600 fs-7">Debug: last Config request payload</summary>
                                    <pre className="sponsored-partners-admin__debug-json bg-light p-3 rounded fs-8">{JSON.stringify(lastRequestPayload, null, 2)}</pre>
                                </details>
                                : null}

                            <div className="sponsored-partners-admin__save-bar">
                                <button className="btn btn-bso flex-grow-1" type="button" disabled={saving || hasAnyUpload} onClick={() => { void savePartner() }}>
                                    {saving ? 'Saving...' : 'Save Sponsored Partners'}
                                </button>
                                <button className="btn btn-light" type="button" onClick={closeEditor}>Clear</button>
                            </div>
                        </div>
                            </>}
                    </aside>
                </div>
            </div>
        </div>
    )
}

function PlanSelector({
    value,
    usages,
    onChange
}: {
    value?: SponsoredPartnerPlanId
    usages: Record<SponsoredPartnerPlanId, number>
    onChange: (planId?: SponsoredPartnerPlanId) => void
}): ReactElement {
    return (
        <div className="sponsored-partners-admin__field">
            <label>Plan</label>
            <select
                value={value ?? ''}
                onChange={(event) => { onChange(event.target.value === '' ? undefined : event.target.value as SponsoredPartnerPlanId) }}
            >
                <option value="">No plan selected / Legacy</option>
                {(['silver', 'gold', 'platinum'] as SponsoredPartnerPlanId[]).map((planId) => {
                    const plan = SPONSORED_PARTNER_PLANS[planId]
                    return <option key={planId} value={planId}>{plan.title} - {plan.priceLabel}</option>
                })}
            </select>
            <div className="sponsored-partners-admin__plan-usage">
                {(['silver', 'gold', 'platinum'] as SponsoredPartnerPlanId[]).map((planId) => {
                    const plan = SPONSORED_PARTNER_PLANS[planId]
                    return (
                        <span
                            key={planId}
                            className={`sponsored-partners-admin__plan-chip${value === planId ? ' sponsored-partners-admin__plan-chip--active' : ''}`}
                        >
                            {plan.title.replace(' Plan', '')}: {usages[planId]} / {plan.activeAdsLimit}
                        </span>
                    )
                })}
            </div>
            {value == null
                ? <div className="sponsored-partners-admin__help">No plan selected / Legacy</div>
                : null}
        </div>
    )
}

function PlanSurfaceSummary({ planId }: { planId?: SponsoredPartnerPlanId }): ReactElement {
    const plan = getPlan(planId)
    return (
        <div className="sponsored-partners-admin__surface-summary">
            {SURFACE_KEYS.map((surfaceKey) => {
                const allowed = plan?.allowedSurfaces[surfaceKey] === true
                return (
                    <span key={surfaceKey} className={`sponsored-partners-admin__surface-pill${allowed ? ' sponsored-partners-admin__surface-pill--allowed' : ''}`}>
                        {allowed ? 'Included' : 'Unavailable'}: {SURFACE_PRESENTATION[surfaceKey].adminTitle}
                    </span>
                )
            })}
        </div>
    )
}

function FormSection({ title, children }: { title: string, children: React.ReactNode }): ReactElement {
    return (
        <section className="sponsored-partners-admin__form-section">
            <h3 className="sponsored-partners-admin__section-title">{title}</h3>
            <div className="sponsored-partners-admin__section-body">{children}</div>
        </section>
    )
}

function PartnerAvatar({ partner, size }: { partner: Pick<SponsoredPartner, 'partnerName' | 'id' | 'logoUrl'>, size: 'sm' | 'lg' }): ReactElement {
    const [failed, setFailed] = useState(false)
    const logo = partner.logoUrl?.trim()
    const showLogo = logo != null && logo !== '' && !failed

    return (
        <span className={`sponsored-partners-admin__avatar sponsored-partners-admin__avatar--${size}`}>
            {showLogo
                ? <img src={logo} alt="" onError={() => { setFailed(true) }} />
                : <span>{getPartnerInitials(partner)}</span>}
        </span>
    )
}

function SurfaceToggle({ surfaceKey, enabled, locked, onChange, children }: { surfaceKey: SurfaceKey, enabled: boolean, locked: boolean, onChange: (enabled: boolean) => void, children: React.ReactNode }): ReactElement {
    const presentation = SURFACE_PRESENTATION[surfaceKey]
    return (
        <section className={`sponsored-partners-admin__surface${locked ? ' sponsored-partners-admin__surface--locked' : ''}`}>
            <div className="sponsored-partners-admin__surface-head">
                <div>
                    <div className="sponsored-partners-admin__surface-name">{presentation.adminTitle}</div>
                    <div className="sponsored-partners-admin__surface-description">{locked ? 'Not included in the selected plan.' : presentation.description}</div>
                </div>
                <label className="sponsored-partners-admin__check">
                    <input type="checkbox" checked={enabled && !locked} disabled={locked} onChange={(event) => { onChange(event.target.checked) }} />
                    <span>{locked ? 'Locked' : enabled ? 'Enabled' : 'Disabled'}</span>
                </label>
            </div>
            {enabled || locked ? children : null}
        </section>
    )
}

function ImageUrlField({
    fieldKey,
    label,
    value,
    uploading,
    error,
    onChange,
    onUploadFile,
    helperText,
    preview
}: {
    fieldKey: string
    label: string
    value: string
    uploading: boolean
    error?: string
    onChange: (value: string) => void
    onUploadFile: (file: File) => Promise<void>
    helperText?: string
    preview?: React.ReactNode
}): ReactElement {
    const inputRef = useRef<HTMLInputElement | null>(null)

    const handleFileChange = async (event: ChangeEvent<HTMLInputElement>): Promise<void> => {
        const file = event.target.files?.[0]
        event.target.value = ''
        if (file == null) return
        try {
            await onUploadFile(file)
        } finally {
            if (inputRef.current != null) inputRef.current.value = ''
        }
    }

    return (
        <div className="sponsored-partners-admin__field">
            <label>{label}</label>
            <div className="sponsored-partners-admin__upload">
                <input value={value} onChange={(event) => { onChange(event.target.value) }} placeholder="https://storage.googleapis.com/..." />
                <button className="btn btn-light btn-sm" type="button" disabled={uploading} onClick={() => { inputRef.current?.click() }}>{uploading ? 'Uploading...' : 'Upload'}</button>
                <input
                    key={fieldKey}
                    ref={inputRef}
                    className="d-none"
                    type="file"
                    accept="image/*"
                    onChange={(event) => { void handleFileChange(event) }}
                />
            </div>
            {helperText != null ? <div className="sponsored-partners-admin__help">{helperText}</div> : null}
            {error != null && error !== '' ? <div className="sponsored-partners-admin__upload-error">{error}</div> : null}
            {preview ?? null}
        </div>
    )
}

function LogoPreview({ value }: { value?: string }): ReactElement {
    const [warning, setWarning] = useState(false)
    const logo = value?.trim() ?? ''

    return (
        <div className="sp-logo-preview">
            <span className="sp-logo-preview__sample">
                {logo !== '' ? <img src={logo} alt="" onLoad={(event) => {
                    const { naturalWidth, naturalHeight } = event.currentTarget
                    const ratio = naturalWidth / naturalHeight
                    setWarning(ratio > 2 || ratio < 0.5)
                }} onError={() => { setWarning(false) }} /> : <span>Partner logo</span>}
            </span>
            <span>Mobile-size preview</span>
            {warning ? <span className="sponsored-partners-admin__soft-warning">This logo may be cropped or appear too small. A square centered icon works best.</span> : null}
        </div>
    )
}

function ExploreHeroFields({
    partner,
    setPartner,
    uploadImageForField,
    uploadingByKey,
    uploadErrors
}: SurfaceFieldsProps): ReactElement | null {
    const surface = partner.surfaces.exploreHero
    if (surface == null) return null

    return (
        <>
            <ImageUrlField
                fieldKey="exploreHero.imageUrl"
                label="Image URL"
                value={surface.imageUrl}
                uploading={uploadingByKey['exploreHero.imageUrl'] === true}
                error={uploadErrors['exploreHero.imageUrl']}
                onChange={(url) => { setPartner((current) => updateSurface(current, 'exploreHero', { imageUrl: url })) }}
                onUploadFile={(file) => uploadImageForField('exploreHero.imageUrl', file, (url) => { setPartner((current) => updateSurface(current, 'exploreHero', { imageUrl: url })) })}
                helperText="Responsive full-width image, rendered at 180 px high in the app. Preferred source ratio: about 2.12:1, for example 460×217 px. The app uses cover cropping, so keep key content centered and avoid important edge detail."
                preview={<ExploreHeroPreview partner={partner} />}
            />
            <TextField label="Title" value={surface.title} limit={32} onChange={(value) => { setPartner((current) => updateSurface(current, 'exploreHero', { title: value })) }} />
            <TextAreaField label="Description" value={surface.description} limit={100} onChange={(value) => { setPartner((current) => updateSurface(current, 'exploreHero', { description: value })) }} />
            <TextField label="Link label" value={surface.linkLabel ?? ''} onChange={(value) => { setPartner((current) => updateSurface(current, 'exploreHero', { linkLabel: value })) }} />
            <TextField label="CTA text" value={surface.ctaText ?? ''} limit={28} onChange={(value) => { setPartner((current) => updateSurface(current, 'exploreHero', { ctaText: value })) }} />
            <NumberField label="Auto-slide interval" value={surface.autoSlideMs} onChange={(value) => { setPartner((current) => updateSurface(current, 'exploreHero', { autoSlideMs: value })) }} />
        </>
    )
}

type SurfaceFieldsProps = {
    partner: SponsoredPartner
    setPartner: React.Dispatch<React.SetStateAction<SponsoredPartner>>
    uploadImageForField: (key: string, file: File, onUrl: (url: string) => void) => Promise<void>
    uploadingByKey: UploadState
    uploadErrors: UploadErrors
}

function InlineFields({
    surfaceKey,
    partner,
    setPartner,
    uploadImageForField,
    uploadingByKey,
    uploadErrors
}: SurfaceFieldsProps & { surfaceKey: 'unitsCatalogueInline' | 'profileListingsInline' }): ReactElement | null {
    const surface = partner.surfaces[surfaceKey]
    if (surface == null) return null

    return (
        <>
            <ImageUrlField
                fieldKey={`${surfaceKey}.imageUrl`}
                label="Image URL"
                value={surface.imageUrl}
                uploading={uploadingByKey[`${surfaceKey}.imageUrl`] === true}
                error={uploadErrors[`${surfaceKey}.imageUrl`]}
                onChange={(url) => { setPartner((current) => updateSurface(current, surfaceKey, { imageUrl: url })) }}
                onUploadFile={(file) => uploadImageForField(`${surfaceKey}.imageUrl`, file, (url) => { setPartner((current) => updateSurface(current, surfaceKey, { imageUrl: url })) })}
                helperText="Recommended visible image area: 172×140 px (1.23:1). Display may shrink responsively to about 150 px wide. The app uses cover cropping; keep key content centered and away from the left edge for arrow/wave variants."
                preview={<InlineMobilePreview partner={partner} surface={surface} />}
            />
            <TextField label="Alt text" value={surface.alt} limit={80} onChange={(value) => { setPartner((current) => updateSurface(current, surfaceKey, { alt: value })) }} />
            <TextField label="Title" value={surface.title ?? ''} limit={20} onChange={(value) => { setPartner((current) => updateSurface(current, surfaceKey, { title: value })) }} />
            <TextAreaField label="Description" value={surface.description ?? ''} limit={45} onChange={(value) => { setPartner((current) => updateSurface(current, surfaceKey, { description: value })) }} />
            <div className="sponsored-partners-admin__field">
                <label>Visual variant</label>
                <div className="sponsored-partners-admin__variant-grid">
                    {INLINE_VARIANTS.map((variant) => (
                        <button
                            type="button"
                            key={variant.value}
                            className={`sponsored-partners-admin__variant${surface.variant === variant.value ? ' sponsored-partners-admin__variant--active' : ''}`}
                            onClick={() => { setPartner((current) => updateSurface(current, surfaceKey, { variant: variant.value })) }}
                        >
                            <span className={`sponsored-partners-admin__variant-swatch sponsored-partners-admin__variant-swatch--${variant.value}`} />
                            {variant.label}
                        </button>
                    ))}
                </div>
            </div>
            <div className="sponsored-partners-admin__row">
                <NumberField label="Show first advertisement after" value={surface.startAfter} onChange={(value) => { setPartner((current) => updateSurface(current, surfaceKey, { startAfter: value })) }} />
                <NumberField label="Repeat every" value={surface.insertEvery} onChange={(value) => { setPartner((current) => updateSurface(current, surfaceKey, { insertEvery: value })) }} />
            </div>
            <NumberField label="Auto-slide interval" value={surface.autoSlideMs} onChange={(value) => { setPartner((current) => updateSurface(current, surfaceKey, { autoSlideMs: value })) }} />
        </>
    )
}

function ModalCardFields({ partner, setPartner }: Pick<SurfaceFieldsProps, 'partner' | 'setPartner'>): ReactElement | null {
    const surface = partner.surfaces.modalCard
    if (surface == null) return null

    const toggleModalSurface = (value: SponsoredModalSurface, checked: boolean): void => {
        const next = checked
            ? Array.from(new Set([...surface.surfaces, value]))
            : surface.surfaces.filter((surfaceValue) => surfaceValue !== value)
        setPartner((current) => updateSurface(current, 'modalCard', { surfaces: next }))
    }

    return (
        <>
            <div className="sponsored-partners-admin__row">
                {MODAL_SURFACES.map((option) => (
                    <label className="sponsored-partners-admin__check" key={option.value}>
                        <input type="checkbox" checked={surface.surfaces.includes(option.value)} onChange={(event) => { toggleModalSurface(option.value, event.target.checked) }} />
                        <span>{option.label}</span>
                    </label>
                ))}
            </div>
            <div className="sponsored-partners-admin__help">Modal card uses the partner logo only. No separate promotional banner image is rendered here.</div>
            <TextField label="Title" value={surface.title} limit={32} onChange={(value) => { setPartner((current) => updateSurface(current, 'modalCard', { title: value })) }} />
            <TextAreaField label="Description" value={surface.description} limit={150} onChange={(value) => { setPartner((current) => updateSurface(current, 'modalCard', { description: value })) }} />
            <TextField label="Link label" value={surface.linkLabel ?? ''} limit={32} onChange={(value) => { setPartner((current) => updateSurface(current, 'modalCard', { linkLabel: value })) }} />
        </>
    )
}

function SponsoredChatFields({
    partner,
    setPartner,
    uploadImageForField,
    uploadingByKey,
    uploadErrors
}: SurfaceFieldsProps): ReactElement | null {
    const surface = partner.surfaces.sponsoredChat
    if (surface == null) return null

    const updateMessage = (index: number, patch: Partial<SponsoredPartnerChatMessage>): void => {
        setPartner((current) => {
            const chat = current.surfaces.sponsoredChat ?? createDefaultSurface('sponsoredChat', current) as SponsoredPartnerChatConfig
            const messages = [...(chat.messages ?? [])]
            messages[index] = { ...messages[index], ...patch }
            return updateSurface(current, 'sponsoredChat', { messages })
        })
    }

    const removeMessage = (index: number): void => {
        setPartner((current) => {
            const chat = current.surfaces.sponsoredChat ?? createDefaultSurface('sponsoredChat', current) as SponsoredPartnerChatConfig
            const messages = (chat.messages ?? []).filter((_, messageIndex) => messageIndex !== index)
            return updateSurface(current, 'sponsoredChat', { messages })
        })
    }

    return (
        <>
            <ImageUrlField
                fieldKey="sponsoredChat.avatarUrl"
                label="Avatar URL"
                value={surface.avatarUrl ?? ''}
                uploading={uploadingByKey['sponsoredChat.avatarUrl'] === true}
                error={uploadErrors['sponsoredChat.avatarUrl']}
                onChange={(url) => { setPartner((current) => updateSurface(current, 'sponsoredChat', { avatarUrl: url })) }}
                onUploadFile={(file) => uploadImageForField('sponsoredChat.avatarUrl', file, (url) => { setPartner((current) => updateSurface(current, 'sponsoredChat', { avatarUrl: url })) })}
                helperText="Displayed as a small chat avatar in the app. A square, centered icon works best."
                preview={<LogoPreview value={surface.avatarUrl} />}
            />
            <TextField label="Title" value={surface.title} limit={28} onChange={(value) => { setPartner((current) => updateSurface(current, 'sponsoredChat', { title: value })) }} />
            <TextField label="List preview" value={surface.listPreview} limit={42} onChange={(value) => { setPartner((current) => updateSurface(current, 'sponsoredChat', { listPreview: value })) }} />
            <TextField label="CTA text" value={surface.ctaText ?? ''} limit={28} onChange={(value) => { setPartner((current) => updateSurface(current, 'sponsoredChat', { ctaText: value })) }} />
            <div className="d-flex justify-content-between align-items-center">
                <strong>Messages</strong>
                <button
                    className="btn btn-light btn-sm"
                    type="button"
                    onClick={() => { setPartner((current) => updateSurface(current, 'sponsoredChat', { messages: [...(surface.messages ?? []), blankChatMessage()] })) }}
                >
                    Add message
                </button>
            </div>
            {(surface.messages ?? []).map((message, index) => (
                <div className="sponsored-partners-admin__message" key={`${message.id}-${index}`}>
                    <div className="d-flex justify-content-between align-items-center">
                        <strong>Message {index + 1}</strong>
                        <button className="btn btn-light-danger btn-sm" type="button" onClick={() => { removeMessage(index) }}>Remove</button>
                    </div>
                    <TextField label="Stable message ID" value={message.id} onChange={(value) => { updateMessage(index, { id: slugify(value) }) }} />
                    <TextField label="Message title" value={message.messageTitle} limit={60} onChange={(value) => { updateMessage(index, { messageTitle: value }) }} />
                    <TextAreaField label="Message body" value={message.messageBody} limit={300} onChange={(value) => { updateMessage(index, { messageBody: value }) }} />
                    <ImageUrlField
                        fieldKey={`sponsoredChat.messages.${index}.imageUrl`}
                        label="Message image URL"
                        value={message.imageUrl ?? ''}
                        uploading={uploadingByKey[`sponsoredChat.messages.${index}.imageUrl`] === true}
                        error={uploadErrors[`sponsoredChat.messages.${index}.imageUrl`]}
                        onChange={(url) => { updateMessage(index, { imageUrl: url }) }}
                        onUploadFile={(file) => uploadImageForField(`sponsoredChat.messages.${index}.imageUrl`, file, (url) => { updateMessage(index, { imageUrl: url }) })}
                        helperText="Responsive image with maximum visible height around 240 px. The app uses cover cropping. Use a landscape image with key content centered."
                        preview={<ChatImagePreview value={message.imageUrl} />}
                    />
                    <TextField label="Card title" value={message.cardTitle ?? ''} limit={60} onChange={(value) => { updateMessage(index, { cardTitle: value }) }} />
                    <TextAreaField label="Card description" value={message.cardDescription ?? ''} limit={180} onChange={(value) => { updateMessage(index, { cardDescription: value }) }} />
                    <TextField label="Link label" value={message.linkLabel ?? ''} limit={28} onChange={(value) => { updateMessage(index, { linkLabel: value }) }} />
                    <TextField label="CTA text" value={message.ctaText ?? ''} limit={28} onChange={(value) => { updateMessage(index, { ctaText: value }) }} />
                </div>
            ))}
        </>
    )
}

function TextField({ label, value, onChange, placeholder, limit }: { label: string, value: string, onChange: (value: string) => void, placeholder?: string, limit?: number }): ReactElement {
    return (
        <div className="sponsored-partners-admin__field">
            <FieldLabel label={label} value={value} limit={limit} />
            <input value={value} placeholder={placeholder} onChange={(event) => { onChange(event.target.value) }} />
        </div>
    )
}

function NumberField({ label, value, onChange }: { label: string, value?: number, onChange: (value: number | undefined) => void }): ReactElement {
    return (
        <div className="sponsored-partners-admin__field">
            <label>{label}</label>
            <input
                type="number"
                min={1}
                value={value ?? ''}
                onChange={(event) => {
                    const next = event.target.value === '' ? undefined : Number(event.target.value)
                    onChange(Number.isFinite(next) ? next : undefined)
                }}
            />
        </div>
    )
}

function TextAreaField({ label, value, onChange, limit }: { label: string, value: string, onChange: (value: string) => void, limit?: number }): ReactElement {
    return (
        <div className="sponsored-partners-admin__field">
            <FieldLabel label={label} value={value} limit={limit} />
            <textarea value={value} onChange={(event) => { onChange(event.target.value) }} />
        </div>
    )
}

function FieldLabel({ label, value, limit }: { label: string, value: string, limit?: number }): ReactElement {
    const overLimit = limit != null && value.length > limit
    return <>
        <label className="sponsored-partners-admin__label-row"><span>{label}</span>{limit != null ? <small>{value.length}/{limit}</small> : null}</label>
        {overLimit ? <div className="sponsored-partners-admin__soft-warning">This text may be truncated in the app.</div> : null}
    </>
}

function ExploreHeroPreview({ partner }: { partner: SponsoredPartner }): ReactElement | null {
    const surface = partner.surfaces.exploreHero
    if (surface == null) return null
    return <div className="sp-preview-banner sp-preview-banner--compact">
        {surface.imageUrl.trim() !== '' ? <img src={surface.imageUrl} alt="" /> : null}
        <div className="sp-preview-banner__content"><strong>{surface.title || 'Ad title'}</strong><span>{surface.description || 'Short description'}</span></div>
    </div>
}

function InlineMobilePreview({ partner, surface }: { partner: SponsoredPartner, surface: NonNullable<SponsoredPartner['surfaces']['unitsCatalogueInline']> }): ReactElement {
    const variant = surface.variant ?? 'yellow-curve'
    return <div className={`sp-inline-mobile sp-inline-mobile--${variant}`}>
        <div className="sp-inline-mobile__copy">
            <span className="sp-inline-mobile__badge">AD</span>
            <div className="sp-inline-mobile__brand">
                {partner.logoUrl?.trim() !== '' ? <img src={partner.logoUrl} alt="" /> : <span>Partner logo</span>}
                <strong>{surface.title || 'Ad title'}</strong>
            </div>
            <p>{surface.description || 'Short description'}</p>
        </div>
        <div className="sp-inline-mobile__image">{surface.imageUrl.trim() !== '' ? <img src={surface.imageUrl} alt={surface.alt} /> : <span>Image</span>}</div>
    </div>
}

function ChatImagePreview({ value }: { value?: string }): ReactElement {
    return <div className="sp-chat-image-preview">{value?.trim() !== '' ? <img src={value} alt="" /> : <span>Message image preview</span>}</div>
}

function PartnerPreviews({ partner }: { partner: SponsoredPartner }): ReactElement {
    const exploreHero = partner.surfaces.exploreHero
    const inlines = [partner.surfaces.unitsCatalogueInline, partner.surfaces.profileListingsInline].filter((surface): surface is NonNullable<SponsoredPartner['surfaces']['unitsCatalogueInline']> => surface?.enabled === true)
    const modalCard = partner.surfaces.modalCard
    const chat = partner.surfaces.sponsoredChat
    const firstMessage = chat?.messages?.[0]

    return (
        <div className="sponsored-partners-admin__previews">
            {exploreHero?.enabled === true ? <ExploreHeroPreview partner={partner} /> : null}
            {inlines.map((inline, index) => <InlineMobilePreview key={index} partner={partner} surface={inline} />)}
            {modalCard?.enabled === true
                ? <div className="sp-preview-modal">
                    <div className="d-flex gap-3 align-items-center">
                        {partner.logoUrl != null && partner.logoUrl.trim() !== '' ? <img src={partner.logoUrl} alt="" className="w-50px h-50px rounded object-fit-cover" /> : <div className="symbol symbol-50px bg-light" />}
                        <div>
                            <strong>{modalCard.title || partner.partnerName || 'Modal title'}</strong>
                            <div className="text-gray-600 fs-7">{modalCard.description || 'Modal card description.'}</div>
                        </div>
                    </div>
                </div>
                : null}
            {chat?.enabled === true
                ? <div className="sp-preview-chat">
                    <div className="mb-2"><strong>{chat.title || partner.partnerName || 'Sponsored Chat'}</strong></div>
                    <div className="sp-preview-chat__bubble">
                        <strong>{firstMessage?.messageTitle || 'Message title'}</strong>
                        <div>{firstMessage?.messageBody || chat.listPreview || 'Message body'}</div>
                        {firstMessage?.imageUrl != null && firstMessage.imageUrl.trim() !== '' ? <img src={firstMessage.imageUrl} alt="" /> : null}
                    </div>
                </div>
                : null}
        </div>
    )
}
